/**
 * Adapter Nominatim (OpenStreetMap). Fallback quando a chave Google falha.
 */
import type { PlaceResult } from './locationPlaces';

const NOMINATIM = 'https://nominatim.openstreetmap.org';
/** Mesmas categorias do nearby Google, nas frases que o Nominatim indexa. */
const OSM_NEARBY_QUERIES = [
    'farmácia',
    'restaurante',
    'supermercado',
    'padaria',
    'igreja',
    'banco',
    'escola',
    'loja',
] as const;

type OsmHit = {
    place_id?: number;
    name?: string;
    display_name?: string;
    lat?: string;
    lon?: string;
    address?: { road?: string; suburb?: string; neighbourhood?: string; city?: string; town?: string };
};

async function fetchOsm<T>(url: string): Promise<T> {
    const controller = new AbortController();
    const timer = globalThis.setTimeout(() => controller.abort(), 10000);
    try {
        const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!res.ok) throw new Error(`Nominatim falhou (${res.status})`);
        return (await res.json()) as T;
    } finally {
        globalThis.clearTimeout(timer);
    }
}

function mapOsmHits(hits: OsmHit[] | undefined): PlaceResult[] {
    const mapped: PlaceResult[] = [];
    for (const hit of hits || []) {
        const latitude = Number(hit.lat);
        const longitude = Number(hit.lon);
        const name = (hit.name || '').trim();
        const address = (hit.display_name || '').trim();
        if (!name || !address || !Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;
        mapped.push({
            id: `osm-${hit.place_id ?? `${latitude},${longitude}`}`,
            name,
            address,
            latitude,
            longitude,
        });
    }
    return mapped;
}

function nominatimSearchUrl(query: string, limit: string, extra: Record<string, string>): string {
    const params = new URLSearchParams({
        format: 'jsonv2',
        q: query,
        limit,
        'accept-language': 'pt-BR',
        addressdetails: '1',
        ...extra,
    });
    return `${NOMINATIM}/search?${params}`;
}

/** POIs num raio curto. Cada categoria que falha volta vazia; as outras seguem. */
export async function searchOsmNearby(near: { latitude: number; longitude: number }): Promise<PlaceResult[]> {
    const delta = 0.02;
    const viewbox = [
        near.longitude - delta,
        near.latitude + delta,
        near.longitude + delta,
        near.latitude - delta,
    ].join(',');
    const batches = await Promise.all(
        OSM_NEARBY_QUERIES.map(async (q) => {
            try {
                return await fetchOsm<OsmHit[]>(nominatimSearchUrl(q, '8', { bounded: '1', viewbox }));
            } catch {
                return [];
            }
        }),
    );
    return mapOsmHits(batches.flat());
}

/** Busca textual limitada ao Brasil. */
export async function searchOsmText(query: string): Promise<PlaceResult[]> {
    const hits = await fetchOsm<OsmHit[]>(nominatimSearchUrl(query, '15', { countrycodes: 'br' }));
    return mapOsmHits(hits);
}

/** Reverse geocode. Mantém o lat/lng do GPS, não o ponto que o Nominatim devolve. */
export async function reverseGeocodeOsm(latitude: number, longitude: number): Promise<PlaceResult> {
    const params = new URLSearchParams({
        format: 'jsonv2',
        lat: String(latitude),
        lon: String(longitude),
        addressdetails: '1',
        'accept-language': 'pt-BR',
    });
    const hit = await fetchOsm<OsmHit>(`${NOMINATIM}/reverse?${params}`);
    const address = (hit.display_name || '').trim();
    if (!address) throw new Error('Endereço não encontrado para estas coordenadas.');
    const road = hit.address?.road || hit.name;
    const locality = hit.address?.suburb || hit.address?.neighbourhood || hit.address?.city || hit.address?.town;
    return {
        id: `osm-${hit.place_id ?? `${latitude},${longitude}`}`,
        name: (road || locality || 'Minha localização').trim(),
        address,
        latitude,
        longitude,
    };
}
